import { ChangeEvent, useContext, useEffect, useMemo } from 'react'
import _ from 'lodash'

import { AnyConceptStatus, ConceptValueDataType, typeHasSubconcepts } from '@fusion-advantage/backcompat'
import { Concept, ConceptActivation, isConceptStatus, isSuperconceptStatus } from '@fusion-advantage/formlib'
import L1Boolean from '../l1/L1Boolean'
import { FusionColor } from '../../../theme/FusionStyle'
import { L4NoPrint } from '../l4/L4Print'
import L6FormContext from '../l6/L6FormContext'

export interface L3BooleanProps {
	xpath: string
	concept: Concept
	subconcept: Concept|null
	conceptStatus: AnyConceptStatus|null
	activation: ConceptActivation|null
	showLabel: boolean
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void
}

export const L3Boolean = ({xpath, concept, subconcept, conceptStatus, activation, showLabel, onChange}: L3BooleanProps) => {
	const checked = useMemo((): boolean => {
		if(isConceptStatus(conceptStatus)) {
			return Boolean(conceptStatus.value)
		}
		if(isSuperconceptStatus(conceptStatus) && subconcept) {
			return Boolean(conceptStatus.subconcepts[subconcept.cid]?.value)
		}
		return false
	}, [conceptStatus, subconcept])
	const shouldPrint = useMemo((): boolean => {
		if(concept.isDx) {
			// per fusion ticket # - enable checked dx's in print view
			//return false
		}
		switch(concept.valueDataType) {
			case ConceptValueDataType.Check:
			case ConceptValueDataType.Checks:
				if(!checked) {
					return false
				}
				break
		}
		return true
	}, [concept, checked])
	const { setXpathValuedDeterminer } = useContext(L6FormContext)
	useEffect(() => {
		if(xpath && checked && shouldPrint) {
			setXpathValuedDeterminer(xpath, true)
			return () => {
				setXpathValuedDeterminer(xpath, false)
			}
		}
	}, [xpath, checked, shouldPrint, setXpathValuedDeterminer ])
	let inputType: string = ''
	let name: string|undefined
	let label: string|undefined
	switch(concept.valueDataType) {
		case ConceptValueDataType.Check:
		case ConceptValueDataType.Checks:
			inputType = 'checkbox'
			break
		case ConceptValueDataType.Radios:
			inputType = 'radio'
			name = concept.cid
			break
	}
	if(showLabel) {
		if(typeHasSubconcepts(concept.valueDataType)) {
			label = subconcept?.title ?? ''
		} else {
			label = concept.title ?? ''
		}
	}
	const boolean = <L1Boolean
		inputType={inputType}
		name={name}
		label={label}
		color={concept.isDx ? FusionColor.DxFuchsia : undefined}
		checked={checked}
		disabled={activation === ConceptActivation.Disabled}
		onChange={onChange}
		dataCid={concept.cid}
	/>
	if(!shouldPrint) {
		return (
			<L4NoPrint>
				{boolean}
			</L4NoPrint>
		)
	}
	return boolean
}

export default L3Boolean
